import React from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Button,
  ButtonGroup,
  Spinner,
  FormText,
  Modal,
  ModalBody,
} from 'reactstrap';
import { useAuth0 } from '@auth0/auth0-react';
import config from '../../config';
import GenQRCode from 'components/Notes/GenQRCode';
import './ProductDetail.css';

const productData = {
  'acrylic': {
    name: 'Acrylic',
    images: [
      require('../../assets/img/acrylic.webp'),
    ],
    description: 'High-quality acrylic sign for indoor/outdoor use.',
    options: [
      { label: 'Small (3.5" x 3.5")', value: 'small', price: 20.00 },
      { label: 'Medium (4" x 4")', value: 'medium', price: 30.00 },
      { label: 'Large (6" x 6")', value: 'large', price: 40.00, disabled: true },
    ]
  },
  'cardecal': {
    name: 'Car Decal',
    images: [require('../../assets/img/carDecal.webp')],
    description: 'Durable vinyl decal for your vehicle.',
    options: [
      { label: 'Small (8" x 8")', value: 'small', price: 30.00, disabled: true },
      { label: 'Medium (10" x 10")', value: 'medium', price: 40.00 },
      { label: 'Large (12" x 12")', value: 'large', price: 50.00 },
    ]
  },
  'magnet': {
    name: 'Magnet',
    images: [require('../../assets/img/carMagnet2.webp')],
    description: 'Removable magnetic',
    options: [
      { label: 'Small (8" x 8")', value: 'small', price: 30.00, disabled: true },
      { label: 'Medium (10" x 10")', value: 'medium', price: 40.00, disabled: true },
      { label: 'Large (12" x 12") 2 Pack', value: 'large', price: 40.00 },
    ]
  },
  'stamp': {
    name: 'Stamp',
    images: [
      require('../../assets/img/quill_stamp2.webp'),
      require('../../assets/img/quill_stamp1.webp')
    ],
    description: 'Self-Inking stamp',
    options: [
      { label: 'Square (1 5/8" x 1 5/8")', value: 'small', price: 35.00 },
    ]
  },
  'golfBallMarker': {
    name: 'Golf Ball Marker',
    images: [
      require('../../assets/img/golfballmarker1.webp'),
      require('../../assets/img/golfballmarker2.webp')
    ],
    description: '',
    options: [
      { label: 'Silver, Regular size', value: 'regular', price: 14.99 },
    ]
  }
};

const ProductDetail = () => {
  const { productId } = useParams();
  const product = productData[productId];
  const images = product.images || [product.image];
  const [selectedImageIndex, setSelectedImageIndex] = React.useState(0);
  const [selectedOption, setSelectedOption] = React.useState(
    product.options.find(opt => !opt.disabled)?.value
  );
  const [quantity, setQuantity] = React.useState(1);
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [isLoading, setIsLoading] = React.useState(false);
  const { state } = useLocation();
  const { guuid, qrColor, logoColor, friendlyName, shortId } = state || {};
  const [modalOpen, setModalOpen] = React.useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = React.useState(false);
  const [confirmMessage, setConfirmMessage] = React.useState('');

  const toggleModal = () => setModalOpen(prev => !prev);

  const handlePurchase = () => {
    let message = `Purchase ${quantity} ${product.name}(s) with option "${selectedOption}"?`;
    if (friendlyName) {
      message = `Confirm ${product.name} purchase for your EXISTING quill named: "${friendlyName}" with ${quantity} items?`;
    }
    setConfirmMessage(message);
    setConfirmModalOpen(true);
  };

  const handleConfirmPurchase = async () => {
    setConfirmModalOpen(false);
    try {
      setIsLoading(true);
      const headers = {
        'Content-Type': 'application/json',
        ...(isAuthenticated && {
          Authorization: `Bearer ${await getAccessTokenSilently()}`
        })
      };

      const response = await fetch(`${config.apiUrl}/${config.apiStage}/create-checkout`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          productId,
          productName: product.name,
          selectedOption,
          quantity,
          ...(friendlyName && { guuid, qrColor, logoColor, friendlyName })
        }),
      });

      if (!response.ok) throw new Error(`Stripe checkout failed. Status: ${response.status}`);
      const { sessionUrl } = await response.json();
      window.location.href = sessionUrl;

    } catch (error) {
      console.error('Checkout error:', error);
      alert('There was a problem starting checkout. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container fluid className="mt-5 mb-5 text-white">
      <Row className="mb-4">
        <Col className="text-center">
          <h2>{product.name}</h2>
          <p>{product.description}</p>
        </Col>
      </Row>

      {/* Quill Info */}
      <Row className="mb-4 justify-content-center align-items-center flex-wrap flex-md-nowrap">
        {friendlyName ? (
          <>
            <Col xs="auto" className="text-center me-3 mb-3 mb-md-0">
              <p className="mb-0">Shopping for Quill:</p>
              <small className="text-muted d-block mt-2">
                Quill will be purchased <br /> with set colors
              </small>
            </Col>
            <Col xs="auto" className="text-center">
              <div className="qr">
                <GenQRCode
                  qrColor={qrColor}
                  size={60}
                  logoColor={logoColor}
                  quill={shortId}
                  friendlyName={friendlyName}
                />
              </div>
              <p className="fw-semibold mb-1">"{friendlyName}"</p>
            </Col>
          </>
        ) : (
          <Col className="text-center">
            <p className="mb-1">Shopping for a New quill!</p>
          </Col>
        )}
      </Row>

      {/* Main Image */}
      <Row className="mb-3 mt-5 justify-content-center">
        <Col className="text-center">
          <img
            src={images[selectedImageIndex]}
            alt={`${product.name} image`}
            className="img-fluid rounded shadow"
            style={{ maxWidth: '300px', maxHeight: '300px' }}
          />
        </Col>
      </Row>

      {/* Thumbnails */}
      {images.length > 1 && (
        <Row className="mb-4 justify-content-center">
          {images.map((imgSrc, idx) => (
            <Col xs="auto" key={idx} className="px-1">
              <img
                src={imgSrc}
                alt={`Thumbnail ${idx + 1}`}
                className={`img-thumbnail shadow ${selectedImageIndex === idx ? 'selected-thumbnail' : ''}`}
                style={{
                  width: '70px',
                  height: '70px',
                  objectFit: 'cover',
                  cursor: 'pointer',
                  borderRadius: '0.5rem',
                  opacity: selectedImageIndex === idx ? 1 : 0.4,
                  transform: selectedImageIndex === idx ? 'scale(1.1)' : 'scale(1)',
                  transition: 'all 0.2s ease-in-out',
                  border: selectedImageIndex === idx ? '3px' : '2px solid transparent',
                  boxShadow: selectedImageIndex === idx
                    ? '0 0 0 4px rgba(13, 110, 253, 0.4)'
                    : 'none',
                }}
                onClick={() => setSelectedImageIndex(idx)}
              />
            </Col>
          ))}
        </Row>
      )}

      {/* Price */}
      <Row className="mb-4">
        <Col className="text-center">
          {selectedOption && (
            <div style={{ fontSize: '1.5rem', fontWeight: '600' }}>
              ${product.options.find(opt => opt.value === selectedOption)?.price.toFixed(2)}
            </div>
          )}
        </Col>
      </Row>

      {/* Option Buttons */}
      <Row className="mb-4">
        <Col className="text-center">
          <h4>Select Option:</h4>
          <ButtonGroup>
            {product.options.map(({ label, value, price, disabled }) => (
              <div key={value} style={{ margin: '0 3px' }}>
                <Button
                  color={value === selectedOption ? 'primary' : 'secondary'}
                  onClick={() => !disabled && setSelectedOption(value)}
                  disabled={disabled}
                  style={{
                    opacity: disabled ? 0.5 : 1,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    minWidth: '120px',
                    padding: '2px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  {label.includes('(') ? (
                    <>
                      <span style={{ fontSize: '1em' }}>
                        {label.split('(')[0].trim()}
                      </span>
                      <span style={{ fontSize: '0.7rem' }}>
                        ({label.split('(')[1]}
                      </span>
                    </>
                  ) : (
                    <span>{label}</span>
                  )}
                  {price && (
                    <span style={{ fontSize: '0.7rem', color: '#ccc', marginTop: '6px' }}>
                      ${price.toFixed(2)}
                    </span>
                  )}
                </Button>
              </div>
            ))}
          </ButtonGroup>
        </Col>
      </Row>

      {/* Quantity Selector */}
      <Row className="mb-4 justify-content-center">
        <Col xs="auto" className="d-flex flex-column align-items-center">
          <h4 className="mb-1">Quantity:</h4>
          <FormText color="muted" className="text-center mb-2">
            All Quills in your order will be identical and connect to the same message.
          </FormText>
          <Button
            style={{
              maxWidth: '90px',
              width: '100%',
              textAlign: 'center',
              color: 'black',
              backgroundColor: 'white',
              border: '1px solid #ccc',
            }}
            onClick={toggleModal}
          >
            {quantity}
          </Button>

          {/* Quantity Modal */}
          <Modal isOpen={modalOpen} toggle={toggleModal} centered size="sm">
            <ModalBody style={{ maxHeight: '200px', overflowY: 'auto', padding: '0' }}>
              {[...Array(10)].map((_, i) => (
                <Button
                  key={i + 1}
                  color="link"
                  onClick={() => {
                    setQuantity(i + 1);
                    toggleModal();
                  }}
                  style={{
                    display: 'block',
                    margin: '5px 0',
                    textAlign: 'center',
                    padding: '5px 10px',
                    width: '100%',
                  }}
                >
                  {i + 1}
                </Button>
              ))}
            </ModalBody>
          </Modal>
        </Col>
      </Row>

      {/* Purchase Button */}
      <Row>
        <Col className="text-center">
          <Button color="success" onClick={handlePurchase}>
            {isLoading ? (
              <>
                <Spinner size="sm" color="light" /> &nbsp;Processing...
              </>
            ) : (
              'Purchase'
            )}
          </Button>
        </Col>
      </Row>

      {/* Confirm Modal */}
      <Modal isOpen={confirmModalOpen} toggle={() => setConfirmModalOpen(false)} centered>
        <ModalBody className="text-center">
          <p className="mb-4 text-black">{confirmMessage}</p>
          <div className="d-flex justify-content-center gap-3">
            <Button color="secondary" onClick={() => setConfirmModalOpen(false)}>Cancel</Button>
            <Button color="primary" onClick={handleConfirmPurchase}>Confirm</Button>
          </div>
        </ModalBody>
      </Modal>
    </Container>
  );
};

export default ProductDetail;